import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { BackendModule, ReadCallback } from 'i18next'
import axios from 'axios'
import { unwrapEnvelopeData } from '@/shared/lib/api-envelope'
import { buildApiUrl } from '@/shared/lib/api-origin'

/**
 * Custom i18next backend for the external upload ("iris") app.
 *
 * The upload flow is token-authenticated and unauthenticated in the usual
 * sense, so the initial load has no tenant context and receives only the
 * global (tenant=NULL) translation bundle. Once the session resolves we know
 * the tenant slug and overlay that tenant's overrides via
 * `loadTenantTranslations` below.
 */
const ApiBackend: BackendModule = {
  type: 'backend',

  init() {
    // no-op
  },

  read(language: string, _namespace: string, callback: ReadCallback) {
    axios
      .get(buildApiUrl('/api/translations/'), {
        params: { language, source: 'FE' },
      })
      .then((res) => {
        callback(null, unwrapEnvelopeData(res.data))
      })
      .catch((err) => {
        console.warn('[i18n] Failed to load translations:', err)
        callback(null, {})
      })
  },
}

i18n
  .use(ApiBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  })

/**
 * Overlay a tenant's translation overrides on top of the global bundle.
 *
 * The upload session response carries the tenant slug; passing it to
 * `/api/translations/?tenant=<slug>` returns the merged (global + tenant)
 * dict, which we deep-merge into the active resource bundle so `t()` picks up
 * the tenant's copy (e.g. "Evidence" → "Documentation").
 */
export async function loadTenantTranslations(
  tenantSlug: string,
  language = 'en',
): Promise<void> {
  const res = await axios.get(buildApiUrl('/api/translations/'), {
    params: { language, source: 'FE', tenant: tenantSlug },
  })
  const data = unwrapEnvelopeData(res.data) as Record<string, string>
  // deep = true, overwrite = true → tenant values replace the global defaults.
  i18n.addResourceBundle(language, 'translation', data, true, true)
}

export default i18n
